import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/client";
import TopBar from "../components/layout/TopBar";
import { ListTodo, Clock, AlertTriangle, FileText, Activity, Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const statCards = [
  { key: "my_open_tasks", label: "My Open Tasks", icon: ListTodo, color: "text-[#4353FF]", bg: "bg-[#EDF0FF]" },
  { key: "due_today", label: "Due Today", icon: Clock, color: "text-[#F59E0B]", bg: "bg-amber-50" },
  { key: "overdue", label: "Overdue", icon: AlertTriangle, color: "text-[#E24A4A]", bg: "bg-red-50" },
  { key: "created_by_me", label: "Created By Me", icon: FileText, color: "text-[#34B56F]", bg: "bg-green-50" },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    API.get("/dashboard").then(r => setData(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <TopBar title="Dashboard" onCreateTask={() => navigate("/tasks?create=true")} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="dashboard-page">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((card) => (
            <div
              key={card.key}
              data-testid={`stat-${card.key}`}
              className="bg-white border border-[#EBEBEB] rounded-md p-4 hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => navigate("/tasks")}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-[#686B74] uppercase tracking-wider">{card.label}</span>
                <div className={`w-8 h-8 rounded-md ${card.bg} flex items-center justify-center`}>
                  <card.icon className={`w-4 h-4 ${card.color}`} />
                </div>
              </div>
              <p className="text-2xl font-bold font-['Manrope'] text-[#111216]">
                {data?.[card.key] ?? "-"}
              </p>
            </div>
          ))}
        </div>

        {/* Activity & Notifications */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-white border border-[#EBEBEB] rounded-md">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#EBEBEB]">
              <Activity className="w-4 h-4 text-[#686B74]" />
              <span className="text-sm font-semibold text-[#111216]">Recent Activity</span>
            </div>
            <div className="divide-y divide-[#EBEBEB] max-h-80 overflow-y-auto">
              {data?.recent_activity?.length ? data.recent_activity.map((a) => (
                <div key={a.id} className="px-4 py-3 hover:bg-[#F9F9FB] transition-colors">
                  <p className="text-sm text-[#111216]">
                    <span className="font-medium">{a.user_name}</span>{" "}
                    <span className="text-[#686B74]">{a.details || a.action_type}</span>
                  </p>
                  <p className="text-xs text-[#686B74] mt-0.5">
                    {a.created_at ? formatDistanceToNow(new Date(a.created_at), { addSuffix: true }) : ""}
                  </p>
                </div>
              )) : (
                <div className="px-4 py-8 text-center text-sm text-[#686B74]">No recent activity</div>
              )}
            </div>
          </div>

          {/* Recent Notifications */}
          <div className="bg-white border border-[#EBEBEB] rounded-md">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#EBEBEB]">
              <Bell className="w-4 h-4 text-[#686B74]" />
              <span className="text-sm font-semibold text-[#111216]">Recent Notifications</span>
            </div>
            <div className="divide-y divide-[#EBEBEB] max-h-80 overflow-y-auto">
              {data?.recent_notifications?.length ? data.recent_notifications.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 hover:bg-[#F9F9FB] transition-colors cursor-pointer ${!n.read ? "bg-[#EDF0FF]/30" : ""}`}
                  onClick={() => navigate(`/tasks/${n.task_id}`)}
                >
                  <p className="text-sm text-[#111216]">{n.message}</p>
                  <p className="text-xs text-[#686B74] mt-0.5">
                    {n.task_title} &middot; {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                  </p>
                </div>
              )) : (
                <div className="px-4 py-8 text-center text-sm text-[#686B74]">No notifications</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
